import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { getPresignedUrl } from '../../../../modules/Company/UploadJourney/components/UploadCard/utils/api/helpers';
import { EXT_MIME, PREVIEWABLE_MIME_TYPES, SPREADSHEET_TYPES } from '../../../DocumentViewer/constants';
import {
    classifyAddable,
    addFileToReports,
    addOriginalXlsxToReports,
    addEditedFileToReports,
    ADD_TO_REPORTS_FAILED_MESSAGE,
} from '../../../Reports/addFileToReports';
import { isEditedWorkspaceSnapshot } from '../../../Reports/editedWorkspaceSnapshot';
import { startAddToWorkspaceTracking, trackUnsavedEditsPrompt } from '../../../Reports/workspaceAnalytics';
import type { WorkspaceFileKind } from '../../../Reports/workspaceAnalytics';
import type { UnsavedEditsPromptAction } from '../../../DocumentViewer/useUnsavedEditsGuard';
import { isReadOnlyAddBand } from '../../../SheetEditor';
import { showAddedToReportsToast } from '../../../Reports/addedToReportsToast';
import { useAddToReportsVisible } from '../../../Reports/useAddToReportsVisible';
import { sheetLoadErrorMessage } from '../../../SheetEditor/sheetSizeGuard';
import { useAppSelector } from '../../../../app/hooks';
import { MagicUploadFileData } from '../../useFetchDataForMU';

export type MagicUploadOpenDrawer = 'docx' | 'sheet' | 'file' | null;

/**
 * Data-Room-parity file-open + Add-to-Reports logic for the Magic Upload
 * files table — mirrors useFetchDataRoomFiles's handleFileClick /
 * handleAddToReports. Routes by extension-first MIME resolution, same as Data
 * Room: docx/txt -> DocxDrawer, xlsx/xls/csv -> SheetEditorDrawer (view-only,
 * no save context), pdf/image -> FileViewerDrawer, anything else -> the
 * caller's existing download behaviour. `downloadTheFile` is MU's own
 * download helper (useFetchDataForMU) — reused here, not duplicated.
 */
const useMagicUploadFileOpen = (downloadTheFile: (docId: number | string, name: string) => Promise<void>) => {
    const [activeFile, setActiveFile] = useState<MagicUploadFileData | null>(null);
    const [openDrawer, setOpenDrawer] = useState<MagicUploadOpenDrawer>(null);
    const [fileUrl, setFileUrl] = useState('');
    const [fileMimeType, setFileMimeType] = useState('');
    const [addingToReports, setAddingToReports] = useState(false);
    const addingToReportsRef = useRef(false);
    const [addedToReports, setAddedToReports] = useState(false);
    const isMountedRef = useRef(true);
    useEffect(
        () => () => {
            isMountedRef.current = false;
        },
        [],
    );

    const user = useAppSelector((state) => state.user.userData);
    const addToReportsVisible = useAddToReportsVisible();

    const closeFile = () => {
        setOpenDrawer(null);
        setActiveFile(null);
        setFileUrl('');
        setFileMimeType('');
    };

    const openFile = async (file: MagicUploadFileData) => {
        const res = await getPresignedUrl({ docId: Number(file.docId) }, false);
        if (!res) {
            toast.error('Error in fetching file');
            return;
        }
        const ext = file.fileName.split('.').pop()?.toLowerCase() ?? '';
        const apiMimeType = res.fileType?.split(';')[0]?.trim().toLowerCase() ?? '';
        // Extension wins for known types, same fallback order as Data Room's handleFileClick.
        const effectiveMimeType = EXT_MIME[ext] ?? apiMimeType;

        if (effectiveMimeType === EXT_MIME.docx || effectiveMimeType === EXT_MIME.txt) {
            setOpenDrawer('docx');
        } else if (SPREADSHEET_TYPES.includes(effectiveMimeType)) {
            setOpenDrawer('sheet');
        } else if (PREVIEWABLE_MIME_TYPES.includes(effectiveMimeType) || effectiveMimeType?.startsWith('image/')) {
            setOpenDrawer('file');
        } else {
            await downloadTheFile(file.docId, file.fileName);
            return;
        }
        setActiveFile(file);
        setFileUrl(res.uri);
        setFileMimeType(effectiveMimeType);
    };

    /** Same gate for the row three-dots "Add to Reports" item and the open
     *  drawers' primary header button — untagged files (no orgId) never qualify. */
    const isAddable = (file: MagicUploadFileData): boolean =>
        addToReportsVisible && !!file.orgId && classifyAddable(file.fileName) !== null;

    /**
     * `payload` arrives from the Sheet/Docx drawer's header button: a
     * read-only-band workbook's bytes, or — when the user changed the file on
     * screen — that snapshot, which is what lands in Workspace.
     * Without it (row three-dots, or an untouched drawer) the ORIGINAL bytes
     * are re-fetched and snapshotted exactly as before. The uploaded file
     * itself is never modified either way.
     */
    const handleAddToReports = async (file: MagicUploadFileData, payload?: unknown) => {
        if (!isAddable(file)) return;
        if (addingToReportsRef.current) return;
        addingToReportsRef.current = true;
        setAddingToReports(true);
        // A fresh add must not read as succeeded off a previous add's lingering
        // "Added" state — the drawers key their close-after-add on this flag.
        setAddedToReports(false);
        const tracker = startAddToWorkspaceTracking(
            { source: 'MAGIC_UPLOAD', fileName: file.fileName, organizationId: file.orgId, user },
            payload,
        );
        try {
            const reportArgs = {
                fileName: file.fileName,
                assesseeOrgId: file.orgId,
                investorOrgId: user?.investorOrgId,
                createdBy: user?.primaryEmail,
            };
            if (isReadOnlyAddBand(payload)) {
                // Drawer-hosted read-only-band Add: the bytes are already in memory.
                await addOriginalXlsxToReports({ ...payload, ...reportArgs });
            } else if (isEditedWorkspaceSnapshot(payload)) {
                await addEditedFileToReports({ ...reportArgs, snapshot: payload });
            } else {
                const res = await getPresignedUrl({ docId: Number(file.docId) }, false);
                if (!res) {
                    toast.error('Error in fetching file');
                    return;
                }
                const fileRes = await fetch(res.uri);
                if (fileRes.status !== 200) {
                    toast.error('Error in fetching file');
                    return;
                }
                const blob = await fileRes.blob();
                tracker.setFileSizeBytes(blob.size);
                await addFileToReports({ ...reportArgs, file: blob, fileType: res.fileType });
            }
            tracker.succeeded();
            showAddedToReportsToast(
                () => window.open(`/investor/app/reports/${file.orgId}`, '_blank', 'noopener,noreferrer'),
                () => {
                    if (isMountedRef.current) setAddedToReports(false);
                },
            );
            setAddedToReports(true);
        } catch (err) {
            console.error('Failed to add Magic Upload file to Reports:', err instanceof Error ? err.message : 'unknown error');
            toast.error(sheetLoadErrorMessage(err, ADD_TO_REPORTS_FAILED_MESSAGE));
        } finally {
            tracker.failed(); // no-op after succeeded(); covers the fetch-error returns above
            addingToReportsRef.current = false;
            setAddingToReports(false);
        }
    };

    /** Analytics for the drawers' unsaved-edits prompt on `file`. */
    const unsavedEditsPromptTracker =
        (file: MagicUploadFileData, fileKind: WorkspaceFileKind) =>
        (action: UnsavedEditsPromptAction): void =>
            trackUnsavedEditsPrompt(action, { source: 'MAGIC_UPLOAD', fileKind, organizationId: file.orgId, user });

    return {
        unsavedEditsPromptTracker,
        activeFile,
        openDrawer,
        fileUrl,
        fileMimeType,
        openFile,
        closeFile,
        isAddable,
        handleAddToReports,
        addingToReports,
        addedToReports,
    };
};

export default useMagicUploadFileOpen;
